import React from "react";

// Shared design system for the flight checkout ("/final") flow — the same
// glass-card / warm-neutral language used by the Interstate checkout, kept in
// one place so every page in this flow looks like the same product instead
// of a patchwork of eras.

export const ORANGE = "#FF6A1A";
export const ORANGE_DARK = "#E8550B";
export const NAVY = "#101535";
export const PAGE_BG = "#EDE8E0";
export const MUTED = "#6C7088";
export const FAINT = "#9CA3AF";
export const HAIRLINE = "#EDE6DC";

export const displayFont = '"Bricolage Grotesque", sans-serif';

// ── Structural ──────────────────────────────────────────────────────

export const SectionLabel = ({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between mb-4">
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: ".5px",
        textTransform: "uppercase",
        color: MUTED,
      }}
    >
      {children}
    </div>
    {action}
  </div>
);

export const Field = ({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <label
      style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".3px",
        textTransform: "uppercase",
        color: MUTED,
        marginBottom: 6,
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

export const inputCls =
  "w-full h-[52px] rounded-[12px] px-4 text-[15px] font-medium text-[#0C1130] outline-none co-input";

// ── Badges / pills ──────────────────────────────────────────────────

export const PillBadge = ({
  children,
  tone = "orange",
}: {
  children: React.ReactNode;
  tone?: "orange" | "neutral" | "success";
}) => {
  const styles =
    tone === "success"
      ? { background: "rgba(34,197,94,.1)", color: "#16A34A", border: "1px solid rgba(34,197,94,.25)" }
      : tone === "neutral"
        ? { background: "rgba(16,21,53,0.05)", color: MUTED, border: "1px solid rgba(16,21,53,0.08)" }
        : { background: "rgba(255,106,26,.1)", color: ORANGE, border: "1px solid rgba(255,106,26,.25)" };

  return (
    <span
      style={{
        ...styles,
        fontSize: 12,
        fontWeight: 700,
        padding: "4px 12px",
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
    </span>
  );
};

export const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div
    className="flex items-center gap-3 rounded-[12px] px-4 py-3"
    style={{ background: "#FAF6F0", border: `1.5px solid ${HAIRLINE}` }}
  >
    <span style={{ color: FAINT, flexShrink: 0 }}>{icon}</span>
    <div className="min-w-0">
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".4px",
          textTransform: "uppercase",
          color: FAINT,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }} className="truncate">
        {value}
      </div>
    </div>
  </div>
);

// ── Buttons ─────────────────────────────────────────────────────────

const Spinner = ({ size = 18, color = "#fff" }: { size?: number; color?: string }) => (
  <svg style={{ width: size, height: size, animation: "co-spin .7s linear infinite" }} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,.35)" strokeWidth="3" fill="none" />
    <path d="M12 2a10 10 0 0110 10" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
  </svg>
);

export const PrimaryButton = ({
  children,
  onClick,
  disabled,
  loading,
  type = "button",
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit";
  className?: string;
}) => {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={className}
      style={{
        width: "100%",
        height: 52,
        borderRadius: 13,
        background: isDisabled ? "#C4C0BC" : ORANGE,
        color: "#fff",
        border: "none",
        fontWeight: 700,
        fontSize: 16,
        cursor: isDisabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        transition: "background .2s",
        boxShadow: isDisabled ? "none" : "0 8px 24px -6px rgba(255,106,26,0.5)",
      }}
      onMouseOver={(e) => {
        if (!isDisabled) (e.currentTarget as HTMLElement).style.background = ORANGE_DARK;
      }}
      onMouseOut={(e) => {
        if (!isDisabled) (e.currentTarget as HTMLElement).style.background = ORANGE;
      }}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
};

export const SecondaryButton = ({
  children,
  onClick,
  disabled,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={className}
    style={{
      width: "100%",
      height: 52,
      borderRadius: 13,
      background: "rgba(255,255,255,.6)",
      color: MUTED,
      border: "1.5px solid rgba(16,21,53,0.1)",
      fontWeight: 600,
      fontSize: 15,
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "inherit",
      opacity: disabled ? 0.6 : 1,
      transition: "background .2s, border-color .2s",
    }}
    onMouseOver={(e) => {
      if (!disabled) (e.currentTarget as HTMLElement).style.borderColor = "rgba(16,21,53,0.22)";
    }}
    onMouseOut={(e) => {
      if (!disabled) (e.currentTarget as HTMLElement).style.borderColor = "rgba(16,21,53,0.1)";
    }}
  >
    {children}
  </button>
);

export const GhostLink = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5"
    style={{
      background: "rgba(255,106,26,.1)",
      border: "1px solid rgba(255,106,26,.22)",
      color: ORANGE,
      fontSize: 12,
      fontWeight: 600,
      padding: "5px 12px",
      borderRadius: 999,
      cursor: "pointer",
      fontFamily: "inherit",
    }}
  >
    {children}
  </button>
);

export const ErrorBanner = ({ children }: { children: React.ReactNode }) => (
  <div
    className="rounded-[14px] p-4 text-sm font-medium"
    style={{
      background: "rgba(254,226,226,0.8)",
      backdropFilter: "blur(10px)",
      border: "1px solid #FECACA",
      color: "#991B1B",
    }}
  >
    {children}
  </div>
);

export const IconChevronDown = ({ open }: { open?: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#9CA3AF"
    strokeWidth="2.2"
    strokeLinecap="round"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform .2s", flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const IconCheck = ({ size = 14, color = "currentColor" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const IconBack = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

// Injected once by index.tsx — keyframes + a couple of helper classes that
// don't have a natural home as inline styles.
export const globalKeyframes = `
  @keyframes co-spin { to { transform: rotate(360deg); } }
  @keyframes co-pop-in { from { opacity:0; transform:scale(.6); } to { opacity:1; transform:scale(1); } }
`;
